/**
 * Defines the PhoneNumberInput component, which integrates with 'mui-tel-input' to provide a phone number input field
 * tailored for international formats. This component extends the functionality with custom validation feedback,
 * allowing for a consistent and intuitive user experience across the application where phone number input is required.
 *
 */
import React from 'react';
import PropTypes from 'prop-types';
import { MuiTelInput } from 'mui-tel-input';

/**
 * Renders an input field for phone numbers that displays customised helper text based on the validation state of the input.
 *
 * @component
 * @param {Object} props - The properties passed to the component
 * @param {string} props.value - The current value of the input
 * @param {Function} props.onChange - Callback function called when the input value changes
 * @param {boolean} [props.error=false] - Indicates whether the input value has validation errors
 * @param {string} [props.helperText=''] - Custom helper text to display beneath the input field
 * @param {string} props.placeholder - Placeholder text to display inside the field initially
 * @returns {JSX.Element} A phone number input field with custom validation and helper text
 */
const PhoneNumberInput = ({
    value,
    onChange,
    error = false,
    valid = true,
    helperText = ' ',
    placeholder,
    ...props
}) => {
    // Determines the helper text to display based on the error state or provided helperText.
    const effectiveHelperText =
        error && !valid ? 'Invalid phone number' : helperText;

    return (
        <MuiTelInput
            defaultCountry='US'
            value={value}
            onChange={(value, info) => onChange(value, info)}
            variant='outlined'
            fullWidth
            placeholder={placeholder}
            forceCallingCode
            helperText={effectiveHelperText}
            error={error}
            sx={{
                '& .MuiOutlinedInput-root': {
                    borderRadius: 50,
                    minHeight: '28px',
                    minWidth: '50px'
                }
            }}
            {...props}
        />
    );
};

PhoneNumberInput.propTypes = {
    value: PropTypes.string.isRequired, // Assuming value is always a string
    onChange: PropTypes.func.isRequired, // Function that handles the change event
    error: PropTypes.bool, // Indicates whether there is an error
    valid: PropTypes.bool,
    helperText: PropTypes.string, // Optional helper text
    placeholder: PropTypes.string.isRequired
};

export default PhoneNumberInput;
